/* script */

import { Vue } from './plugins'
import BaseResource from '../resources/base'
import ResourceAttribute from './ResourceAttribute'
import { ResourceListAction, ResourceListField, ResourceListFilterManager } from './core/resource_list'
import { uniq, uniqBy } from 'lodash'
import assert from 'assert'

export { ResourceListAction, ResourceListField, ResourceListFilterManager }

const __vue_script__ = Vue.extend({
    name: 'ResourceList',
    components: {
        ResourceAttribute,
    },
    computed: {
        tableClasses() {
            let classes = ['resource-table']

            if (this.loading) {
                classes.push('loading')
            }

            if (this.selectable) {
                classes.push('selectable')
            }

            return classes
        },
        columnCount() {
            if (!this.fields) {
                return 0
            }

            let count = this.fields.length
            if (this.actions.length > 0) {
                count++
            }

            return count
        },
        editableFilters() {
            return this.filters.filter((filter) => filter.config.editable)
        },
        selectedResourcesIds() {
            return this.selectedResources.map((resource) => resource.id)
        },
    },
    props: {
        resourceClass: {
            type: Function,
            required: true,
            validator(value) {
                return value.prototype && value.prototype instanceof BaseResource
            },
        },
        resolveAttributes: {
            type: Array,
            default() {
                return []
            },
        },
        value: {
            type: Array,
            default() {
                return []
            },
        },
        title: {
            type: String,
            default: '',
        },
        fields: {
            type: Array,
            required: true,
        },
        search: {
            type: [String, Boolean],
            default: '',
        },
        selectable: {
            type: Boolean,
        },
        searchParameter: {
            type: String,
            default: 'search',
        },
        filters: {
            type: Array,
            default() {
                return []
            },
            validator(value) {
                for (let i in value) {
                    let isInstance = value[i] instanceof ResourceListFilterManager
                    if (!isInstance) {
                        return false
                    }
                }
                return true
            },
        },
        actions: {
            type: Array,
            required: false,
            default() {
                return []
            },
            validator(value) {
                for (let i in value) {
                    let isInstance = value[i] instanceof ResourceListAction
                    if (!isInstance) {
                        return false
                    }
                }
                return true
            },
        },
        all: {
            type: Boolean,
            default: false,
        },
        page: {
            type: [Number, String],
            default: 1,
        },
        pageSize: {
            type: [Number, String],
            default: 20,
        },
        noPaginate: {
            type: Boolean,
            default: false,
        },
        loadingText: {
            type: String,
            default: 'Loading...',
        },
    },
    watch: {
        pageData() {
            this.selectedResources = []
            this.$emit('page', this.pageData)
            window.scroll(0, 0)
        },
    },
    data() {
        return {
            loading: true,
            pageData: null,
            pageResources: [],
            selectedResources: [],
            selectedAction: null,
            actionLoading: false,
            searchQuery: this.search,
        }
    },
    methods: {
        async withPageData(fn) {
            this.loading = true
            this.pageData = await fn(this.pageData)
            this.pageResources = this.pageData.resources
            await this.doResolveAttributes()
            this.loading = false
        },
        async nextPage() {
            return this.withPageData((pageData) => pageData.nextPage())
        },
        async prevPage() {
            return this.withPageData((pageData) => pageData.prevPage())
        },
        async onPageChange(num) {
            if (Number(num) !== Number(this.pageData.page)) {
                this.withPageData((pageData) => pageData.goToPage(num))
            }
        },
        async doResolveAttributes() {
            let lookupPromises = []

            this.pageResources.forEach((resource) => {
                this.resolveAttributes.forEach((fieldName) => {
                    lookupPromises.push(resource.resolveAttribute(fieldName))
                })
            })

            return await Promise.all(lookupPromises)
        },
        resetActions(clearSelected = true) {
            this.selectedAction = null
            if (clearSelected) {
                this.selectedResources = []
            }
        },
        async onActionFormSubmit(e) {
            let action = this.selectedAction
            if (action && this.selectedResources.length > 0) {
                this.actionLoading = true
                // Run the action handler
                await action.handler(this.selectedResources)
                this.actionLoading = false
                // It's just nicer to show what action was selected for a second
                setTimeout(() => this.resetActions(action.reset), 1000)
            } else if (action) {
                throw new Error(`Can't run action "${action.name}": no resources were selected`)
            } else {
                throw new Error('Action criteria is invalid!')
            }
        },
        selectAll() {
            this.selectedResources = uniqBy(this.selectedResources.concat(this.pageResources), (resource) => resource.id)
        },
        showFilters() {
            this.$refs.filtersModal.show()
        },
        hideFilters() {
            this.$refs.filtersModal.hide()
        },
        applyFilters() {
            this.list()
            this.hideFilters()
        },
        onSearch() {
            this.list()
        },
        onSearchKeyUp(e) {
            if (e.code.toLowerCase() === 'escape') {
                this.searchQuery = ''
                this.list()
            }
        },
        onRowClick(e, resource) {
            if (this.selectable) {
                this.selectedResources = [resource]
                this.$emit('input', this.selectedResources)
            }
        },
        getQueryObject() {
            let baseQuery = { page_size: this.pageSize, page: this.page }
            let filters = ResourceListFilterManager.mapped(this.filters)
            if (this.search !== false && this.searchQuery.length > 0) {
                filters[this.searchParameter] = this.searchQuery
            }

            return Object.assign(filters, baseQuery)
        },
        async list() {
            let ResourceClass = this.resourceClass
            this.loading = true
            // Enable ResourceClass.all() to be called instead of list()
            if (this.all) {
                this.pageResources = await ResourceClass.all({ query: this.getQueryObject() })
                this.pageData = null
            } else {
                this.pageData = await ResourceClass.list({ query: this.getQueryObject() })
                this.pageResources = this.pageData.resources
            }

            await this.doResolveAttributes()
            this.loading = false
        },
    },
    beforeMount() {
        if (this.actions && this.actions.length > 0 && this.selectable) {
            throw new Error('ResourceList: "selectable" is not compatible with "actions", you must choose one or the other')
        }
    },
    async mounted() {
        await this.list()
    },
})

/* template */
var __vue_render__ = function() {
  var _vm = this
  var _h = _vm.$createElement
  var _c = _vm._self._c || _h
  return _c(
    "div",
    { staticClass: "resource-table-wrapper" },
    [
      _c(
        "b-table-simple",
        {
          class: _vm.tableClasses,
          attrs: { hover: "", small: "", "caption-top": "", responsive: "" }
        },
        [
          _c(
            "b-thead",
            { attrs: { "head-variant": "light" } },
            [
              _vm.title
                ? _c(
                    "b-tr",
                    [
                      _c(
                        "b-th",
                        {
                          staticClass: "resource-table-head",
                          attrs: { colspan: _vm.columnCount }
                        },
                        [
                          _c(
                            "b-row",
                            [
                              _c(
                                "b-col",
                                [
                                  _vm._t("title", [
                                    _c("h6", { staticClass: "title" }, [
                                      _vm._v(_vm._s(_vm.title))
                                    ])
                                  ])
                                ],
                                2
                              )
                            ],
                            1
                          )
                        ],
                        1
                      )
                    ],
                    1
                  )
                : _vm._e(),
              _vm._v(" "),
              _vm.search !== false || _vm.editableFilters.length > 0
                ? _c(
                    "b-tr",
                    [
                      _c(
                        "b-th",
                        {
                          staticClass: "resource-table-head",
                          attrs: { colspan: _vm.columnCount }
                        },
                        [
                          _c(
                            "b-form",
                            {
                              attrs: { inline: "" },
                              on: {
                                submit: function($event) {
                                  $event.preventDefault()
                                  return _vm.onSearch($event)
                                }
                              }
                            },
                            [
                              _vm._t("filters-before"),
                              _vm._v(" "),
                              _vm.search !== false
                                ? _c("b-form-input", {
                                    attrs: { placeholder: "Search" },
                                    on: { keyup: _vm.onSearchKeyUp },
                                    model: {
                                      value: _vm.searchQuery,
                                      callback: function($$v) {
                                        _vm.searchQuery = $$v
                                      },
                                      expression: "searchQuery"
                                    }
                                  })
                                : _vm._e(),
                              _vm._v(" "),
                              _vm.search !== false
                                ? _c(
                                    "b-button",
                                    {
                                      staticClass: "ml-2",
                                      on: { click: _vm.onSearch }
                                    },
                                    [
                                      _c("b-icon", {
                                        attrs: { icon: "search" }
                                      })
                                    ],
                                    1
                                  )
                                : _vm._e(),
                              _vm._v(" "),
                              _vm.editableFilters.length > 0
                                ? _c(
                                    "b-button",
                                    {
                                      staticClass: "ml-2",
                                      on: { click: _vm.showFilters }
                                    },
                                    [
                                      _c("b-icon", {
                                        attrs: { icon: "filter" }
                                      }),
                                      _vm._v(" Filters")
                                    ],
                                    1
                                  )
                                : _vm._e(),
                              _vm._v(" "),
                              _vm._t("filters-after")
                            ],
                            2
                          )
                        ],
                        1
                      )
                    ],
                    1
                  )
                : _vm._e()
            ],
            1
          ),
          _vm._v(" "),
          _c(
            "b-thead",
            { attrs: { "head-variant": "light" } },
            [
              _c(
                "b-tr",
                [
                  _vm.actions.length > 0
                    ? _c("th", { attrs: { width: "1" } }, [_vm._v(" ")])
                    : _vm._e(),
                  _vm._v(" "),
                  _vm._l(_vm.fields, function(field, i) {
                    return _c(
                      "th",
                      {
                        key: i,
                        class: field.options.className,
                        attrs: { width: field.options.width }
                      },
                      [
                        _vm._t("header-" + field.key, [
                          _vm._v(
                            "\n                        " +
                              _vm._s(field) +
                              "\n                    "
                          )
                        ])
                      ],
                      2
                    )
                  })
                ],
                2
              )
            ],
            1
          ),
          _vm._v(" "),
          !_vm.loading
            ? _c(
                "b-tbody",
                _vm._l(_vm.pageResources, function(resource, i) {
                  return _c(
                    "b-tr",
                    {
                      key: i,
                      class: ~_vm.selectedResourcesIds.indexOf(resource.id)
                        ? "selected"
                        : "",
                      on: {
                        click: function(e) {
                          return _vm.onRowClick(e, resource)
                        }
                      }
                    },
                    [
                      _vm.actions.length > 0
                        ? _c(
                            "td",
                            [
                              _c("b-form-checkbox", {
                                attrs: { value: resource },
                                model: {
                                  value: _vm.selectedResources,
                                  callback: function($$v) {
                                    _vm.selectedResources = $$v
                                  },
                                  expression: "selectedResources"
                                }
                              })
                            ],
                            1
                          )
                        : _vm._e(),
                      _vm._v(" "),
                      _vm._l(_vm.fields, function(field, j) {
                        return _c(
                          "b-td",
                          { key: j },
                          [
                            _vm._t(
                              "value-" + field.key,
                              [
                                _c(field.component, {
                                  tag: "component",
                                  attrs: {
                                    resource: resource,
                                    attribute: field.key,
                                    "loading-text": _vm.loadingText
                                  }
                                })
                              ],
                              { field: field, resource: resource, index: i }
                            )
                          ],
                          2
                        )
                      })
                    ],
                    2
                  )
                }),
                1
              )
            : _c("b-tbody", [
                _c("tr", [
                  _c(
                    "td",
                    {
                      staticClass: "loading text-muted",
                      attrs: { colspan: _vm.columnCount }
                    },
                    [_vm._v("Loading...")]
                  )
                ])
              ])
        ],
        1
      ),
      _vm._v(" "),
      _vm.actions.length > 0 && !_vm.loading
        ? _c(
            "div",
            { staticClass: "with-selected" },
            [
              _vm.selectedResources.length > 0
                ? _c(
                    "form",
                    {
                      staticClass: "form-inline",
                      on: {
                        submit: function($event) {
                          $event.preventDefault()
                          return _vm.onActionFormSubmit($event)
                        }
                      }
                    },
                    [
                      _vm.actions.length > 0
                        ? _c(
                            "select",
                            {
                              directives: [
                                {
                                  name: "model",
                                  rawName: "v-model",
                                  value: _vm.selectedAction,
                                  expression: "selectedAction"
                                }
                              ],
                              staticClass: "form-control mr-2 col-md-3",
                              attrs: { placeholder: "With selected..." },
                              on: {
                                change: function($event) {
                                  var $$selectedVal = Array.prototype.filter
                                    .call($event.target.options, function(o) {
                                      return o.selected
                                    })
                                    .map(function(o) {
                                      var val =
                                        "_value" in o ? o._value : o.value
                                      return val
                                    })
                                  _vm.selectedAction = $event.target.multiple
                                    ? $$selectedVal
                                    : $$selectedVal[0]
                                }
                              }
                            },
                            [
                              _c(
                                "optgroup",
                                {
                                  attrs: {
                                    label:
                                      "With Selected (" +
                                      _vm.selectedResources.length +
                                      ")"
                                  }
                                },
                                _vm._l(_vm.actions, function(action, i) {
                                  return _c(
                                    "option",
                                    {
                                      key: i,
                                      attrs: {
                                        disabled:
                                          !action.isValid(
                                            _vm.selectedResources
                                          ) || _vm.actionLoading
                                      },
                                      domProps: { value: action }
                                    },
                                    [
                                      _vm._v(
                                        "\n                        " +
                                          _vm._s(action) +
                                          "\n                    "
                                      )
                                    ]
                                  )
                                }),
                                0
                              )
                            ]
                          )
                        : _vm._e(),
                      _vm._v(" "),
                      _c(
                        "div",
                        { staticClass: "btn-group", attrs: { role: "group" } },
                        [
                          _c(
                            "b-button",
                            {
                              attrs: {
                                type: "submit",
                                variant: "primary",
                                disabled: !_vm.selectedAction
                              }
                            },
                            [_vm._v("Go")]
                          ),
                          _vm._v(" "),
                          _c(
                            "b-button",
                            {
                              attrs: { variant: "light" },
                              on: { click: _vm.selectAll }
                            },
                            [_vm._v("Select All")]
                          ),
                          _vm._v(" "),
                          _c(
                            "b-button",
                            {
                              attrs: { variant: "light" },
                              on: { click: _vm.resetActions }
                            },
                            [_vm._v("Reset")]
                          )
                        ],
                        1
                      )
                    ]
                  )
                : _c(
                    "b-button-group",
                    [
                      _c(
                        "b-button",
                        { attrs: { variant: "light", disabled: "" } },
                        [_c("b-icon-arrow-return-right")],
                        1
                      ),
                      _vm._v(" "),
                      _c(
                        "b-button",
                        {
                          attrs: { variant: "light" },
                          on: { click: _vm.selectAll }
                        },
                        [_vm._v("Select All")]
                      )
                    ],
                    1
                  )
            ],
            1
          )
        : _vm._e(),
      _vm._v(" "),
      !_vm.noPaginate && _vm.pageData && _vm.pageData.pages > 1
        ? _c("b-pagination", {
            attrs: {
              value: _vm.pageData.page,
              "total-rows": _vm.pageData.count,
              "per-page": _vm.pageData.pageSize,
              disabled: _vm.loading,
              "prev-text": "Prev",
              "next-text": "Next",
              align: "center",
              "hide-ellipsis": ""
            },
            on: { input: _vm.onPageChange }
          })
        : _vm._e(),
      _vm._v(" "),
      _c(
        "b-modal",
        {
          ref: "filtersModal",
          attrs: { title: "Filter Results", centered: "", "no-fade": "" }
        },
        [
          _vm.search !== false
            ? _c(
                "b-form-group",
                { attrs: { label: "Search Query" } },
                [
                  _c("b-form-input", {
                    model: {
                      value: _vm.searchQuery,
                      callback: function($$v) {
                        _vm.searchQuery = $$v
                      },
                      expression: "searchQuery"
                    }
                  })
                ],
                1
              )
            : _vm._e(),
          _vm._v(" "),
          _vm._l(_vm.editableFilters, function(filter, i) {
            return _c(
              "div",
              { key: i },
              [
                _c(
                  "b-form-group",
                  { attrs: { label: filter.name } },
                  [
                    _c(filter.component, {
                      tag: "component",
                      model: {
                        value: filter.value,
                        callback: function($$v) {
                          _vm.$set(filter, "value", $$v)
                        },
                        expression: "filter.value"
                      }
                    })
                  ],
                  1
                )
              ],
              1
            )
          }),
          _vm._v(" "),
          _vm._t("filters-footer"),
          _vm._v(" "),
          _c(
            "template",
            { slot: "modal-footer" },
            [
              _c("b-button", { on: { click: _vm.applyFilters } }, [
                _vm._v("Apply")
              ])
            ],
            1
          )
        ],
        2
      )
    ],
    1
  )
}
var __vue_staticRenderFns__ = []
__vue_render__._withStripped = true

  /* style */
  const __vue_inject_styles__ = function (inject) {
    if (!inject) return
    inject("data-v-54ada3a4_0", { source: ".resource-table-wrapper[data-v-54ada3a4] {\n  position: relative !important;\n}\n.resource-table-wrapper .title[data-v-54ada3a4] {\n  line-height: 2.4rem;\n  margin: 0;\n  font-weight: bold;\n}\n.resource-table-wrapper td.loading[data-v-54ada3a4] {\n  line-height: 120px;\n}\n.resource-table-wrapper .resource-table table[data-v-54ada3a4] {\n  width: 100%;\n  min-height: 450px;\n  max-height: 800px;\n}\n.resource-table-wrapper .resource-table table tr.selected[data-v-54ada3a4] {\n  background-color: rgba(0, 0, 0, 0.075);\n}\n.resource-table-wrapper .resource-table.selectable td[data-v-54ada3a4] {\n  cursor: pointer;\n}\n.resource-table-wrapper .resource-table-head[data-v-54ada3a4] {\n  overflow: hidden;\n}\n\n", map: "AAiYA;EACA,6BAAA;AAhYA;AAkYA;EACA,mBAAA;EACA,SAAA;EACA,iBAAA;AAhYA;AAmYA;EACA,kBAAA;AAjYA;AAqYA;EACA,WAAA;EACA,iBAAA;EACA,iBAAA;AAnYA;AAqYA;EACA,sCAAA;AAnYA;AAuYA;EACA,eAAA;AArYA;AAyYA;EACA,gBAAA;AAvYA;;AAEA,0CAA0C", media: undefined })

  }
  /* scoped */
  const __vue_scope_id__ = "data-v-54ada3a4"
  /* module identifier */
  const __vue_module_identifier__ = undefined
  /* functional template */
  const __vue_is_functional_template__ = undefined
  /* component normalizer */
  function __vue_normalize__(
    template, style, script,
    scope, functional, moduleIdentifier, shadowMode,
    createInjector, createInjectorSSR, createInjectorShadow
  ) {
    const component = (typeof script === 'function' ? script.options : script) || {}

    // For security concerns, we use only base name in production mode.
    component.__file = "/src/components/ResourceList.vue"

    if (!component.render) {
      component.render = template.render
      component.staticRenderFns = template.staticRenderFns
      component._compiled = true

      if (functional) component.functional = true
    }

    component._scopeId = scope

    if (true) {
      let hook
      if (false) {
        // In SSR.
        hook = function(context) {
          // 2.3 injection
          context =
            context || // cached call
            (this.$vnode && this.$vnode.ssrContext) || // stateful
            (this.parent && this.parent.$vnode && this.parent.$vnode.ssrContext) // functional
          // 2.2 with runInNewContext: true
          if (!context && typeof __VUE_SSR_CONTEXT__ !== 'undefined') {
            context = __VUE_SSR_CONTEXT__
          }
          // inject component styles
          if (style) {
            style.call(this, createInjectorSSR(context))
          }
          // register component module identifier for async chunk inference
          if (context && context._registeredComponents) {
            context._registeredComponents.add(moduleIdentifier)
          }
        }
        // used by ssr in case component is cached and beforeCreate
        // never gets called
        component._ssrRegister = hook
      }
      else if (style) {
        hook = shadowMode 
          ? function(context) {
              style.call(this, createInjectorShadow(context, this.$root.$options.shadowRoot))
            }
          : function(context) {
              style.call(this, createInjector(context))
            }
      }

      if (hook !== undefined) {
        if (component.functional) {
          // register for functional component in vue file
          const originalRender = component.render
          component.render = function renderWithStyleInjection(h, context) {
            hook.call(context)
            return originalRender(h, context)
          }
        } else {
          // inject component registration as beforeCreate hook
          const existing = component.beforeCreate
          component.beforeCreate = existing ? [].concat(existing, hook) : [hook]
        }
      }
    }

    return component
  }
  /* style inject */
  function __vue_create_injector__() {
    const styles = __vue_create_injector__.styles || (__vue_create_injector__.styles = {})
    const isOldIE =
      typeof navigator !== 'undefined' &&
      /msie [6-9]\\b/.test(navigator.userAgent.toLowerCase())

    return function addStyle(id, css) {
      if (document.querySelector('style[data-vue-ssr-id~="' + id + '"]')) return // SSR styles are present.

      const group = isOldIE ? css.media || 'default' : id
      const style = styles[group] || (styles[group] = { ids: [], parts: [], element: undefined })

      if (!style.ids.includes(id)) {
        let code = css.source
        let index = style.ids.length

        style.ids.push(id)

        if (false && css.map) {
          // https://developer.chrome.com/devtools/docs/javascript-debugging
          // this makes source maps inside style tags work properly in Chrome
          code += '\n/*# sourceURL=' + css.map.sources[0] + ' */'
          // http://stackoverflow.com/a/26603875
          code +=
            '\n/*# sourceMappingURL=data:application/json;base64,' +
            btoa(unescape(encodeURIComponent(JSON.stringify(css.map)))) +
            ' */'
        }

        if (isOldIE) {
          style.element = style.element || document.querySelector('style[data-group=' + group + ']')
        }

        if (!style.element) {
          const head = document.head || document.getElementsByTagName('head')[0]
          const el = style.element = document.createElement('style')
          el.type = 'text/css'

          if (css.media) el.setAttribute('media', css.media)
          if (isOldIE) {
            el.setAttribute('data-group', group)
            el.setAttribute('data-next-index', '0')
          }

          head.appendChild(el)
        }

        if (isOldIE) {
          index = parseInt(style.element.getAttribute('data-next-index'))
          style.element.setAttribute('data-next-index', index + 1)
        }

        if (style.element.styleSheet) {
          style.parts.push(code)
          style.element.styleSheet.cssText = style.parts
            .filter(Boolean)
            .join('\n')
        } else {
          const textNode = document.createTextNode(code)
          const nodes = style.element.childNodes
          if (nodes[index]) style.element.removeChild(nodes[index])
          if (nodes.length) style.element.insertBefore(textNode, nodes[index])
          else style.element.appendChild(textNode)
        }
      }
    }
  }
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__ = /*#__PURE__*/__vue_normalize__(
    { render: __vue_render__, staticRenderFns: __vue_staticRenderFns__ },
    __vue_inject_styles__,
    __vue_script__,
    __vue_scope_id__,
    __vue_is_functional_template__,
    __vue_module_identifier__,
    false,
    __vue_create_injector__,
    undefined,
    undefined
  )

  export default __vue_component__


//////////////////
// WEBPACK FOOTER
// ./~/@sigmacloud/sigma-client/dist/components/ResourceList.js
// module id = wyGz
// module chunks = 0